// Password generator + sandbox-regression test.
//
// Strategy: reject sampling. Sample a candidate from a sensible
// per-position alphabet derived from the rules; check passesAll;
// repeat up to MAX_ATTEMPTS. Returns null if no candidate is found.
//
// The sandbox test in the andon loop reduces to: "can this rule set
// produce a password in fewer than MAX_SANDBOX_ATTEMPTS tries?" When
// the AI proposes a rule that contradicts existing rules (or makes
// the search space too narrow), this test fails cleanly and the
// proposal gets rejected before it can land. That's the architectural
// gate the demo is trying to make visible.

import { charsOfClass, evaluate, passesAll, type CharClass, type Rule } from "./dsl";

const DEFAULT_LENGTH = 8;
const DEFAULT_ALPHABET: CharClass[] = ["lower", "digit"];

/** Generation attempt budget. Plenty for any reasonable rule set; if
 *  we blow past this we're either generating against a contradictory
 *  ruleset (sandbox test fails) or one whose search space is too narrow
 *  for the alphabet (AI needs a redesign). */
const MAX_ATTEMPTS = 20000;

/** Sandbox budget — tighter than the runtime budget on purpose. A
 *  rule that takes most of this budget to generate is technically valid
 *  but slow; we'd rather reject it at the sandbox gate than ship it
 *  and have the runtime hitch.
 *
 *  Sized so that a sandbox FAILURE means "this ruleset is genuinely
 *  unsatisfiable (or near enough)" — not "reject-sampling got unlucky on
 *  a small space." That's the whole point of the demo: rejections should
 *  be real andon events the harness caught, not noise. Cheap regardless —
 *  each attempt is a few-dozen-char string + a handful of predicate
 *  checks, so even the full budget is sub-millisecond-scale per call. */
const MAX_SANDBOX_ATTEMPTS = 6000;

// ─── Inference of length + alphabet from the ruleset ────────────────────────

function inferLength(rules: readonly Rule[]): number {
  // Equality wins; otherwise take the largest lower bound, otherwise default.
  let lowerBound = 0;
  for (const r of rules) {
    if (r.predicate.kind !== "LENGTH") continue;
    const { op, n } = r.predicate;
    if (op === "=") return n;
    if (op === ">=" && n > lowerBound) lowerBound = n;
    if (op === ">" && n + 1 > lowerBound) lowerBound = n + 1;
  }
  return lowerBound > 0 ? lowerBound : DEFAULT_LENGTH;
}

function inferAlphabet(rules: readonly Rule[]): string {
  // Union of CONTAINS / STARTS_WITH / POSITION classes; default if none.
  const classes = new Set<CharClass>();
  for (const r of rules) {
    const p = r.predicate;
    if (p.kind === "CONTAINS" || p.kind === "STARTS_WITH" || p.kind === "POSITION") {
      classes.add(p.class);
    }
  }
  if (classes.size === 0) DEFAULT_ALPHABET.forEach((c) => classes.add(c));
  let alphabet = "";
  for (const c of classes) alphabet += charsOfClass(c);
  return alphabet;
}

function pickRandom(alphabet: string): string {
  return alphabet[Math.floor(Math.random() * alphabet.length)];
}

/** Targeted per-position alphabet: if a POSITION rule pins index i to
 *  a class, sample only from that class for that index. Otherwise use
 *  the union alphabet. STARTS_WITH counts as POSITION 0. */
function alphabetForPosition(
  rules: readonly Rule[],
  i: number,
  fallback: string,
): string {
  for (const r of rules) {
    const p = r.predicate;
    if (p.kind === "POSITION" && p.i === i) return charsOfClass(p.class);
    if (p.kind === "STARTS_WITH" && i === 0) return charsOfClass(p.class);
  }
  return fallback;
}

// ─── Generation ─────────────────────────────────────────────────────────────

/** Try to generate a password that passes every rule. Returns null
 *  if the budget is exhausted (sandbox failure or contradiction). */
export function generate(rules: readonly Rule[], budget = MAX_ATTEMPTS): string | null {
  const length = inferLength(rules);
  const fallback = inferAlphabet(rules);
  if (length === 0 || fallback.length === 0) return null;

  for (let attempt = 0; attempt < budget; attempt++) {
    let candidate = "";
    for (let i = 0; i < length; i++) {
      candidate += pickRandom(alphabetForPosition(rules, i, fallback));
    }
    if (passesAll(rules, candidate)) return candidate;
  }
  return null;
}

// ─── Sandbox regression test ────────────────────────────────────────────────

export interface SandboxResult {
  /** True when a candidate password was found within the sandbox budget. */
  passed: boolean;
  /** A sample password the candidate ruleset produces (when passed=true).
   *  Null when the test failed. Useful for the ledger entry's payload. */
  sample: string | null;
  /** Attempts used. Surfaced for the UI so flaky-but-passing rules
   *  can be flagged ("passed in 1400 tries — barely viable"). */
  attemptsUsed: number;
}

/** Test whether a proposed ruleset can still generate a password.
 *  This is the gate that catches contradictory or over-constraining
 *  proposals from the AI before they hit production. */
export function sandboxTest(rules: readonly Rule[]): SandboxResult {
  const length = inferLength(rules);
  const fallback = inferAlphabet(rules);
  if (length === 0 || fallback.length === 0) {
    return { passed: false, sample: null, attemptsUsed: 0 };
  }
  for (let attempt = 0; attempt < MAX_SANDBOX_ATTEMPTS; attempt++) {
    let candidate = "";
    for (let i = 0; i < length; i++) {
      candidate += pickRandom(alphabetForPosition(rules, i, fallback));
    }
    if (passesAll(rules, candidate)) {
      return { passed: true, sample: candidate, attemptsUsed: attempt + 1 };
    }
  }
  return { passed: false, sample: null, attemptsUsed: MAX_SANDBOX_ATTEMPTS };
}

// ─── Useful for the UI later ────────────────────────────────────────────────

/** Per-rule pass/fail breakdown — for showing the user WHICH constraints
 *  their guess violated. Pure function over the rule set and candidate. */
export function explainCandidate(
  rules: readonly Rule[],
  candidate: string,
): Array<{ rule: Rule; passed: boolean }> {
  return rules.map((rule) => ({ rule, passed: evaluate(rule.predicate, candidate) }));
}
